import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Phone, Clock, CheckCircle2, X, ExternalLink, RefreshCw, ArrowRight, UserCheck } from 'lucide-react';
import { theme, cardStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  CHECK-IN QUEUE — admin sees who self-checked-in via kiosk/mobile
// ═══════════════════════════════════════════════════════════════════

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtAgo  = (d) => {
  if (!d) return '';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ${m % 60}m ago`;
};

const REASON_ICON = { appointment: '📅', 'walk-in': '🚶', 'follow-up': '↩️', emergency: '🚨', consultation: '💬' };

const CheckinQueuePage = ({ API_BASE, currentUser }) => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('waiting');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/checkins/today`);
      setList(r.data || []);
    } catch {}
    setLoading(false);
  }, [API_BASE]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const filtered = filter === 'all' ? list : list.filter(c => c.status === filter);

  const action = async (id, kind) => {
    try {
      await axios.post(`${API_BASE}/checkins/${id}/${kind}`, { by: currentUser?.fullName || 'staff' });
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const kioskUrl = `${window.location.protocol}//${window.location.hostname}:5000/kiosk`;

  const counts = {
    waiting: list.filter(c => c.status === 'waiting').length,
    'in-treatment': list.filter(c => c.status === 'in-treatment').length,
    completed: list.filter(c => c.status === 'completed').length,
    cancelled: list.filter(c => c.status === 'cancelled').length,
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Users size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Check-In Queue</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              <strong>{counts.waiting}</strong> waiting · {counts['in-treatment']} in chair · {counts.completed} done · auto-refresh 30s
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a href={kioskUrl} target="_blank" rel="noreferrer" style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              <ExternalLink size={14} style={{ marginRight: 6 }} /> Open Kiosk
            </a>
            <button onClick={load} style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
              <RefreshCw size={14} style={{ marginRight: 4 }} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, padding: 12, marginBottom: 14, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <strong>📱 Tablet Setup:</strong> Browser kholo aur ye URL bookmark karo:
        <code style={{ background: 'white', padding: '3px 8px', borderRadius: 4, marginLeft: 6, fontSize: 12 }}>{kioskUrl}</code>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {[
          { k: 'waiting',     l: '⏳ Waiting',     c: counts.waiting,         color: '#f59e0b' },
          { k: 'in-treatment', l: '🦷 In Chair',   c: counts['in-treatment'], color: '#2563eb' },
          { k: 'completed',   l: '✓ Completed',    c: counts.completed,       color: '#16a34a' },
          { k: 'cancelled',   l: '✗ Cancelled',    c: counts.cancelled,       color: '#dc2626' },
          { k: 'all',         l: 'All',            c: list.length,            color: theme.primary },
        ].map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} style={{
            padding: '7px 14px', borderRadius: 999,
            border: `1.5px solid ${filter === f.k ? f.color : theme.border}`,
            background: filter === f.k ? f.color + '18' : 'white',
            color: filter === f.k ? f.color : theme.textMuted,
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>{f.l} <span style={{ marginLeft: 4, background: 'rgba(0,0,0,0.08)', padding: '1px 7px', borderRadius: 999, fontSize: 11 }}>{f.c}</span></button>
        ))}
      </div>

      {loading ? <div style={{ ...cardStyle, padding: 30, textAlign: 'center' }}>Loading…</div>
       : filtered.length === 0 ? (
          <div style={{ ...cardStyle, padding: 50, textAlign: 'center' }}>
            <Users size={40} color={theme.textSubtle} style={{ marginBottom: 8, opacity: 0.5 }} />
            <p style={{ color: theme.textMuted, fontWeight: 600 }}>{filter === 'waiting' ? 'No one is waiting' : 'No check-ins'}</p>
          </div>
       ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filtered.map(c => {
            const isWaiting = c.status === 'waiting';
            const isIn = c.status === 'in-treatment';
            const accent = isWaiting ? '#f59e0b' : isIn ? '#2563eb' : c.status === 'completed' ? '#16a34a' : '#94a3b8';
            return (
              <div key={c._id} style={{ ...cardStyle, padding: 14, borderLeft: `4px solid ${accent}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{REASON_ICON[c.reason] || '👤'}</span>
                      <div style={{ fontWeight: 700, fontSize: 15, color: theme.navy }}>{c.patientName}</div>
                      <span style={{ fontSize: 11, color: theme.textMuted }}>{c.patientUhid}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: theme.textMuted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {c.patientPhone && <span><Phone size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{c.patientPhone}</span>}
                      <span><Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{fmtTime(c.checkedInAt)} ({fmtAgo(c.checkedInAt)})</span>
                      <span style={{ fontWeight: 600, color: accent, textTransform: 'uppercase' }}>{c.reason}</span>
                      <span style={{ color: theme.textSubtle }}>via {c.source}</span>
                    </div>
                    {c.acknowledgedBy && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 3 }}>✓ Acknowledged by {c.acknowledgedBy}</div>}
                  </div>

                  <div style={{ display: 'flex', gap: 6 }}>
                    {isWaiting && !c.acknowledgedBy && (
                      <button onClick={() => action(c._id, 'acknowledge')} style={actBtn}>
                        <UserCheck size={12} /> Ack
                      </button>
                    )}
                    {isWaiting && (
                      <button onClick={() => action(c._id, 'call-in')} style={{ ...actBtn, background: '#2563eb', color: 'white', borderColor: '#2563eb' }}>
                        <ArrowRight size={12} /> Call In
                      </button>
                    )}
                    {isIn && (
                      <button onClick={() => action(c._id, 'complete')} style={{ ...actBtn, background: '#16a34a', color: 'white', borderColor: '#16a34a' }}>
                        <CheckCircle2 size={12} /> Done
                      </button>
                    )}
                    {(isWaiting || isIn) && (
                      <button onClick={() => action(c._id, 'cancel')} style={{ ...actBtn, color: theme.danger, borderColor: theme.danger + '55' }}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const actBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 12, fontWeight: 700 };

export default CheckinQueuePage;
